// 상단 지점 이동 드롭다운(#958) — DB branches 목록을 화면용으로 정리한다.
// /network 페이지와 같은 거르기: 꺼진 지점(isActive false)·지역 "테스트"·묶음에 없는 지역 제외, 지역별로 묶고 이름순.

// [지역 묶음, DB region 값들, 언어별 이름]
const REGIONS = [
  ["서울", ["서울"], { en: "Seoul", ja: "ソウル", zh: "首尔", th: "โซล" }],
  ["경기/인천", ["경기", "인천"], { en: "Gyeonggi / Incheon", ja: "京畿/仁川", zh: "京畿/仁川", th: "คยองกี / อินชอน" }],
  ["충청", ["충청"], { en: "Chungcheong", ja: "忠清", zh: "忠清", th: "ชุงชอง" }],
  ["경상", ["경상", "부산", "대구"], { en: "Gyeongsang", ja: "慶尚", zh: "庆尚", th: "คยองซัง" }],
  ["전라", ["전라"], { en: "Jeolla", ja: "全羅", zh: "全罗", th: "ชอลลา" }],
  ["강원", ["강원"], { en: "Gangwon", ja: "江原", zh: "江原", th: "คังวอน" }],
  ["제주", ["제주"], { en: "Jeju", ja: "済州", zh: "济州", th: "เชจู" }],
];

// DB 가 공개 쓰기라 주소를 믿지 않는다 — 끗 지점 홈페이지 첫 화면(https://www.kkeutclinic[-지점].co.kr)만 링크로 건다.
const BRANCH_HOME = /^https:\/\/(www\.)?kkeutclinic(-[a-z0-9]+)?\.co\.kr\/?$/i;

/** 다국어 칸이면 그 언어, 비었으면 한국어. 글자가 아닌 값은 "" */
export function branchText(v, locale = "ko") {
  const s = typeof v === "string" ? v : [v?.[locale], v?.ko].find((x) => typeof x === "string" && x.trim());
  return typeof s === "string" ? s.trim() : "";
}

/** "천호 (10월 26일 오픈 예정)" → "천호". 그 밖의 괄호("구로(본점)")는 둔다. */
export function withoutOpeningNote(name = "") {
  return name.replace(/\s*[(（][^)）]*(오픈\s*예정|opening soon|coming soon)[^)）]*[)）]/gi, "").trim();
}

// 오픈 예정 표시 (#979) — 관리자 지점 관리의 「오픈 예정」·오픈 날짜(admin#351)
const MON_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MON_TH = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const OPENING = {
  ko: [(m, d) => `${m}월 ${d}일 오픈 예정`, "오픈 예정"],
  en: [(m, d) => `Opening ${MON_EN[m - 1]} ${d}`, "Opening soon"],
  ja: [(m, d) => `${m}月${d}日オープン予定`, "オープン予定"],
  zh: [(m, d) => `${m}月${d}日开业`, "即将开业"],
  th: [(m, d) => `เปิด ${d} ${MON_TH[m - 1]}`, "เร็วๆ นี้"],
};
const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const todayKst = () => new Date(Date.now() + 9 * 3600e3).toISOString().slice(0, 10);

/** 「10월 26일 오픈 예정」·「Opening soon」… 오픈 예정이 아니거나 오픈 날짜(한국 시간)가 되면 null */
export function openingLabel(branch, locale = "ko", today = todayKst()) {
  if (branch?.openingSoon !== true) return null;
  const d = typeof branch.openingDate === "string" && DATE_RE.test(branch.openingDate) ? branch.openingDate : "";
  if (d && d <= today) return null;
  const [withDate, plain] = OPENING[locale] || OPENING.en;
  return d ? withDate(+d.slice(5, 7), +d.slice(8, 10)) : plain;
}

/**
 * 전 지점 온라인 예약 화면 — groupBranches 에 예약 주소·운영시간(각 지점 「기본 설정」)을 더한다.
 * hours = { [지점 id]: { weekdayHours, weekendHours, holidayHours } }. 외국어 칸이 비면 한국어.
 * 예약 주소는 그 지점 홈페이지의 /reserve (외국어면 /en/reserve 처럼 언어를 붙인다). href 없으면(링크 끄기) null.
 */
export function reserveBranchGroups(docs = [], hours = {}, locale = "ko") {
  const prefix = locale === "ko" ? "" : `/${locale}`;
  return groupBranches(docs, "", locale).map((g) => ({
    ...g,
    items: g.items.map((b) => ({
      ...b,
      reserveHref: b.href && `${b.href.replace(/\/$/, "")}${prefix}/reserve`,
      wk: branchText(hours[b.id]?.weekdayHours, locale),
      we: branchText(hours[b.id]?.weekendHours, locale),
      ho: branchText(hours[b.id]?.holidayHours, locale),
    })),
  }));
}

// 전 지점 카카오톡 상담 (#1029) — 「기본 설정」 카카오 링크. 공개 쓰기 DB 라 카카오 채널 주소만, http 는 https 로.
const KAKAO_CHANNEL = /^https?:\/\/pf\.kakao\.com\/_[A-Za-z0-9]+(\/chat)?\/?$/;

/** groupBranches + kakaoHref(카카오 채널 주소, 없거나 링크 끄기면 null) */
export function kakaoBranchGroups(docs = [], info = {}, locale = "ko") {
  return groupBranches(docs, "", locale).map((g) => ({
    ...g,
    items: g.items.map((b) => {
      const link = branchText(info[b.id]?.kakaoLink);
      return { ...b, kakaoHref: b.href && KAKAO_CHANNEL.test(link) ? link.replace(/^http:/, "https:") : null };
    }),
  }));
}

/**
 * branches 문서들 → [{ key, label, items: [{ id, name, opening, href, current }] }]
 * href 는 끗 지점 홈페이지 주소일 때만, linkDisabled 면 null(회색·클릭 불가).
 */
export function groupBranches(docs = [], currentId = "", locale = "ko") {
  const sorted = docs
    .filter((b) => b && b.isActive !== false)
    .sort((a, b) => (branchText(a.name) || a.id).localeCompare(branchText(b.name) || b.id, "ko"));
  return REGIONS.map(([key, raws, names]) => ({
    key,
    label: locale === "ko" ? key : names[locale] || names.en,
    items: sorted
      .filter((b) => raws.includes(branchText(b.region)))
      .map((b) => {
        const url = branchText(b.url, locale);
        return {
          id: b.id,
          name: withoutOpeningNote(branchText(b.name, locale)) || b.id,
          opening: openingLabel(b, locale),
          href: b.linkDisabled !== true && BRANCH_HOME.test(url) ? url : null,
          current: b.id === currentId,
        };
      }),
  })).filter((g) => g.items.length);
}
